import React, { useEffect } from 'react';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import SharedButton from '../../components/Shared/SharedButton';
import DropdownComponent from '../../components/Shared/DropdownComponent';
import DatePicker from '../../components/Shared/DatePicker';
import PageHeader from '../../components/Shared/PageHeader';
import CustomSearchDropdown from '../../components/Shared/CustomSearchDropdown';
import { getAllEmployees } from '../../store/slices/EmployeeSlice';
import { CreateNewLeave, UpdateLeave } from '../../store/slices/HrSlice';
import { FaCheckDouble } from 'react-icons/fa6';

export default function CreateLeave() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const { editingLeave } = location.state || {};
  const { user } = useSelector(state => state.auth);
  const { isLoading } = useSelector(state => state.employee);
  const { isLoading: loading } = useSelector(state => state?.hr);
  const { allEmployees } = useSelector(state => state.employee);

  const listEmployees = allEmployees?.data
    ? allEmployees.data.map(employee => ({
      id: employee.id,
      name: `${employee.first_name} ${employee.last_name}`
    }))
    : [];

  useEffect(() => {
    dispatch(getAllEmployees());
  }, [dispatch]);

  const initialValues = {
    id: editingLeave?.id || '',
    employee_id: editingLeave?.employee_id || '',
    leave_type: editingLeave?.leave_type || '',
    start_date: editingLeave?.start_date ? new Date(editingLeave.start_date) : null,
    end_date: editingLeave?.end_date ? new Date(editingLeave.end_date) : null,
    status: editingLeave?.status || '',
    branch_id: editingLeave?.branch_id || user?.branch_id,
    business_id: editingLeave?.business_id || user?.business_id
  };

  const validationSchema = Yup.object({
    employee_id: Yup.number().required(t('required')),
    leave_type: Yup.string().required(t('required')),
    start_date: Yup.date().required(t('required')),
    end_date: Yup.date().required(t('required')),
    status: Yup.string().required(t('required'))
  });

  const leave_type_option = [
    { value: 'Day', label: 'Day' },
    { value: 'Week', label: 'Week' },
    { value: 'Month', label: 'Month' },
    { value: 'Annual', label: 'Annual' }
  ];

  const status_option = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' }
  ];

  const onSubmit = async (values, { resetForm }) => {
    try {
      const formattedData = {
        ...values,
        start_date: values.start_date ? new Date(values.start_date).toISOString().split('T')[0] : '',
        end_date: values.end_date ? new Date(values.end_date).toISOString().split('T')[0] : '',
      };

      const resultAction = values.id
        ? await dispatch(UpdateLeave(formattedData))
        : await dispatch(CreateNewLeave(formattedData));

      if (UpdateLeave.fulfilled.match(resultAction) || CreateNewLeave.fulfilled.match(resultAction)) {
        resetForm();
        navigate(-1);
      }
    } catch (error) {
      console.error('Error submitting leave:', error);
    }
  };

  return (
    <div className="flex flex-col w-full gap-4 max-h-[calc(100vh-150px)]">
      <PageHeader
        title={t('Create Leave')}
        showBack
        onBack={() => navigate(-1)}
      />

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {(formik) => (
          <Form onSubmit={formik.handleSubmit} className="space-y-4">
            <div className='grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 w-full'>
              <CustomSearchDropdown
                name='employee_id'
                placeholder={`${t('select')} ${t('employee')}`}
                options={listEmployees}
                label={`${t('employee')} ${t('name')}`}
                value={formik.values.employee_id}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                className={'!w-full !border-gray-300 !bg-gray-100 !h-[30px]'}
              />

              <DropdownComponent
                label={t('leave_type')}
                name={'leave_type'}
                placeholder={t('select leave type')}
                options={leave_type_option}
                className='!w-full'
              />

              <DatePicker
                label={t('start_date')}
                name={'start_date'}
                placeholder={t('Enter start_date')}
                className='!w-full'
              />

              <DatePicker
                label={t('end_date')}
                name={'end_date'}
                placeholder={t('Enter end_date')}
                className='!w-full'
              />

              <DropdownComponent
                label={t('status')}
                name={'status'}
                options={status_option}
                placeholder={t("select status")}
                className='!w-full'
              />
            </div>

            <div className='flex justify-end gap-2 '>
              <SharedButton
                type='button'
                icon='pi pi-times'
                label={t('Cancel')}
                buttonType='reset'
                className='cancel-btn'
                onClick={() => {
                  navigate(-1);
                }}
                disabled={loading}
              />
              <SharedButton
                type='submit'
                icon={<FaCheckDouble size={12} className='me-2' />}
                loading={loading}
                disabled={loading}
                label={t('Submit')}
                className='submit-btn'
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
}